export type CustomerAllocationInput = {
  id: string;
  saleId: string;
  movementId: string;
  amount: number;
};

export type CustomerSaleForVoid = {
  id: string;
  total: number;
  paidAmount: number;
  createdAt: string;
};

export type CustomerChargeInput = {
  customerId: string;
  amount: number;
};

export type CustomerRefifoTarget = {
  saleId: string;
  shortId: string;
  amount: number;
  movementId: string;
};

export type CustomerReleasedAllocation = {
  id: string;
  movementId: string;
  amount: number;
};

export type CustomerVoidPlan = {
  voidedSaleId: string;
  releasedAllocations: CustomerReleasedAllocation[];
  refifoTargets: CustomerRefifoTarget[];
  newAllocations: Array<{
    id: string;
    saleId: string;
    movementId: string;
    amount: number;
  }>;
  compensatingPayments: Array<{
    customerId: string;
    amount: number;
    movementId: string;
  }>;
  affectedSaleIds: string[];
  hasCustomerImpact: boolean;
  totalReleased: number;
  totalRefifo: number;
};

const shortId = (id: string) => (id.length > 6 ? id.slice(-6) : id);

/**
 * Plan puro de unwind customer al anular una venta: libera imputaciones y re-FIFO a deudas más antiguas.
 */
export function buildCustomerVoidPlan(params: {
  voidedSaleId: string;
  allocations: CustomerAllocationInput[];
  customerSales: CustomerSaleForVoid[];
  charges: CustomerChargeInput[];
  nowIso?: string;
  uuidForComp?: (seed: string) => string;
  uuidForNew?: () => string;
}): CustomerVoidPlan {
  const {
    voidedSaleId,
    allocations,
    customerSales,
    charges,
    uuidForComp = () => crypto.randomUUID(),
    uuidForNew = () => crypto.randomUUID(),
  } = params;

  const released = allocations.filter((a) => a.saleId === voidedSaleId && a.amount > 0);
  const salePaidMap = new Map<string, number>();
  for (const sale of customerSales) {
    if (sale.id === voidedSaleId) continue;
    salePaidMap.set(sale.id, sale.paidAmount);
  }

  const otherSales = customerSales
    .filter((s) => s.id !== voidedSaleId)
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));

  const refifoTargets: CustomerRefifoTarget[] = [];
  const newAllocations: CustomerVoidPlan['newAllocations'] = [];

  for (const alloc of released) {
    let remaining = alloc.amount;
    for (const sale of otherSales) {
      if (remaining <= 0) break;
      const paid = salePaidMap.get(sale.id) ?? 0;
      const pending = sale.total - paid;
      if (pending <= 0) continue;
      const slice = Math.min(remaining, pending);
      const newId = uuidForNew();
      newAllocations.push({
        id: newId,
        saleId: sale.id,
        movementId: alloc.movementId,
        amount: slice,
      });
      refifoTargets.push({
        saleId: sale.id,
        shortId: shortId(sale.id),
        amount: slice,
        movementId: alloc.movementId,
      });
      salePaidMap.set(sale.id, paid + slice);
      remaining -= slice;
    }
  }

  const compensatingPayments = charges
    .filter((c) => c.customerId && c.amount > 0)
    .map((c) => ({
      customerId: c.customerId,
      amount: c.amount,
      movementId: uuidForComp(`${voidedSaleId}:void-comp:${c.customerId}:${c.amount}`),
    }));

  const affectedSaleIds = Array.from(
    new Set([voidedSaleId, ...refifoTargets.map((t) => t.saleId)])
  );

  const totalReleased = released.reduce((s, a) => s + a.amount, 0);
  const totalRefifo = refifoTargets.reduce((s, t) => s + t.amount, 0);

  return {
    voidedSaleId,
    releasedAllocations: released.map((a) => ({
      id: a.id,
      movementId: a.movementId,
      amount: a.amount,
    })),
    refifoTargets,
    newAllocations,
    compensatingPayments,
    affectedSaleIds,
    hasCustomerImpact: released.length > 0 || compensatingPayments.length > 0,
    totalReleased,
    totalRefifo,
  };
}

export type SaleDetailForCustomerVoidPreview = {
  id: string;
  fiadoCustomerId?: string | null;
  customerAllocationPaid?: number;
  payments: Array<{ methodId: string; amount: number; customerId?: string | null }>;
};

/**
 * Preview simplificado para ventas solo-remotas (sin SQLite local).
 * No calcula FIFO detallado; informa cargo customer y montos imputados conocidos.
 */
export function buildCustomerVoidPlanFromSaleDetail(
  detail: SaleDetailForCustomerVoidPreview
): CustomerVoidPlan | null {
  const customerPayment = detail.payments.find((p) => p.methodId === 'fiado');
  const chargeAmount = customerPayment?.amount ?? 0;
  const customerId = detail.fiadoCustomerId ?? customerPayment?.customerId ?? null;
  const totalReleased = detail.customerAllocationPaid ?? 0;

  if (chargeAmount <= 0 && totalReleased <= 0) {
    return null;
  }

  const compensatingPayments =
    chargeAmount > 0 && customerId
      ? [{ customerId, amount: chargeAmount, movementId: 'remote-preview' }]
      : [];

  return {
    voidedSaleId: detail.id,
    releasedAllocations:
      totalReleased > 0
        ? [{ id: 'remote-preview', movementId: 'remote-preview', amount: totalReleased }]
        : [],
    refifoTargets: [],
    newAllocations: [],
    compensatingPayments,
    affectedSaleIds: [detail.id],
    hasCustomerImpact: compensatingPayments.length > 0 || totalReleased > 0,
    totalReleased,
    totalRefifo: 0,
  };
}
